/**
 * Permit Revision Request — Phase 1D
 * ─────────────────────────────────────────────────────────────────────
 * Structured coordination layer for municipality-issued permit revisions.
 *
 * Replaces free-text "Revision Requested" notes with a deterministic
 * shape: who issued it, when, severity, what items must be resolved,
 * and who owns each one.
 *
 * Invariants:
 * 1. Optional until first revision exists. `permitDetail.revisionRequest`
 *    is only present once a coordinator opens one.
 * 2. Append-safe item shape — adding items never disturbs prior items.
 *    Resolved state is the only per-item mutation (toggle on/off via
 *    `setRevisionItemResolved`).
 * 3. Pure derivation only. `resolveRevisionReadiness` never mutates the
 *    job, never advances stage, never writes to any other surface.
 * 4. NO automation: no notifications, no auto-stage progression, no
 *    municipality reminders, no AI parsing. Coordination only.
 *
 * UI lives in `components/permitting/PermitRevisionPanel.tsx`. All
 * mutations route through this module so the structured contract
 * cannot be bypassed.
 */
import { z } from "zod";
import type { Job, PermitDetail } from "@/data/types";

export type RevisionSeverity = "minor" | "standard" | "critical";

export const REVISION_SEVERITIES: RevisionSeverity[] = ["minor", "standard", "critical"];

export const REVISION_SEVERITY_LABEL: Record<RevisionSeverity, string> = {
  minor: "Minor",
  standard: "Standard",
  critical: "Critical",
};

export interface RevisionItem {
  id: string;
  documentType: string;
  issueDescription: string;
  requiredAction: string;
  resolved: boolean;
  resolvedAt?: string;
  resolvedBy?: string;
}

export interface RevisionRequest {
  municipalityReferenceNumber?: string;
  reviewerName?: string;
  reviewerDepartment?: string;
  /** ISO datetime — when the city's revision packet was received. */
  receivedDate: string;
  /** ISO datetime — city-imposed response deadline (optional). */
  responseDeadline?: string;
  severity: RevisionSeverity;
  assignedOwner?: string;
  notes?: string;
  items: RevisionItem[];
}

// ── Validation schemas ──────────────────────────────────────────────

const MAX_FREEFORM = 500;
const MAX_SHORT = 160;

export const revisionItemInputSchema = z.object({
  documentType: z.string().trim().min(1, "Document type is required").max(MAX_SHORT),
  issueDescription: z.string().trim().min(1, "Describe what the city flagged").max(MAX_FREEFORM),
  requiredAction: z.string().trim().min(1, "What must be done?").max(MAX_FREEFORM),
});

export type RevisionItemInput = z.infer<typeof revisionItemInputSchema>;

export const revisionRequestMetadataSchema = z.object({
  municipalityReferenceNumber: z.string().trim().max(MAX_SHORT).optional().or(z.literal("").transform(() => undefined)),
  reviewerName: z.string().trim().max(MAX_SHORT).optional().or(z.literal("").transform(() => undefined)),
  reviewerDepartment: z.string().trim().max(MAX_SHORT).optional().or(z.literal("").transform(() => undefined)),
  receivedDate: z.string().datetime({ offset: true, message: "Received date must be a full ISO datetime" }),
  responseDeadline: z
    .string()
    .datetime({ offset: true, message: "Deadline must be a full ISO datetime" })
    .optional()
    .or(z.literal("").transform(() => undefined)),
  severity: z.enum(["minor", "standard", "critical"]),
  assignedOwner: z.string().trim().max(MAX_SHORT).optional().or(z.literal("").transform(() => undefined)),
  notes: z.string().trim().max(MAX_FREEFORM).optional().or(z.literal("").transform(() => undefined)),
});

export type RevisionRequestMetadataInput = z.infer<typeof revisionRequestMetadataSchema>;

// ── ID generator ────────────────────────────────────────────────────

function makeRevisionItemId(): string {
  const rand = Math.random().toString(36).slice(2, 8);
  return `ri-${Date.now()}-${rand}`;
}

// ── Read accessors ──────────────────────────────────────────────────

export function getRevisionRequest(job: Job | null | undefined): RevisionRequest | null {
  return job?.permitDetail?.revisionRequest ?? null;
}

export function hasRevisionRequest(job: Job | null | undefined): boolean {
  return !!job?.permitDetail?.revisionRequest;
}

// ── Derivation ──────────────────────────────────────────────────────

export type RevisionReadinessState = "neutral" | "not_ready" | "ready";
export type RevisionReadinessTone = "neutral" | "amber" | "red" | "green";

export interface RevisionReadiness {
  state: RevisionReadinessState;
  tone: RevisionReadinessTone;
  totalItems: number;
  resolvedCount: number;
  unresolvedCount: number;
  unresolvedCriticalCount: number;
  /** UI-ready label (e.g. "2 / 5 resolved"). */
  progressLabel: string;
  /** UI-ready badge ("Ready to resubmit", "Resubmission not ready", "—"). */
  badgeLabel: string;
}

export function resolveRevisionReadiness(
  request: RevisionRequest | null | undefined,
): RevisionReadiness {
  if (!request || request.items.length === 0) {
    return {
      state: "neutral",
      tone: "neutral",
      totalItems: 0,
      resolvedCount: 0,
      unresolvedCount: 0,
      unresolvedCriticalCount: 0,
      progressLabel: "0 / 0 resolved",
      badgeLabel: "—",
    };
  }
  const total = request.items.length;
  const resolved = request.items.filter((i) => i.resolved).length;
  const unresolved = total - resolved;
  const unresolvedCritical = request.severity === "critical" && unresolved > 0 ? unresolved : 0;
  const allResolved = unresolved === 0;
  const tone: RevisionReadinessTone = allResolved
    ? "green"
    : unresolvedCritical > 0
      ? "red"
      : "amber";
  return {
    state: allResolved ? "ready" : "not_ready",
    tone,
    totalItems: total,
    resolvedCount: resolved,
    unresolvedCount: unresolved,
    unresolvedCriticalCount: unresolvedCritical,
    progressLabel: `${resolved} / ${total} resolved`,
    badgeLabel: allResolved ? "Ready to resubmit" : "Resubmission not ready",
  };
}

// ── Mutators ────────────────────────────────────────────────────────

function withPermitDetail(job: Job, mutate: (pd: PermitDetail) => PermitDetail): Job {
  if (!job.permitDetail) return job;
  return { ...job, permitDetail: mutate(job.permitDetail) };
}

/**
 * Initialize a structured revision request on a job. If one already
 * exists, this is a metadata-only update (items preserved).
 */
export function setRevisionRequestMetadata(
  job: Job,
  input: RevisionRequestMetadataInput,
): Job {
  const parsed = revisionRequestMetadataSchema.parse(input);
  return withPermitDetail(job, (pd) => {
    const existing = pd.revisionRequest;
    const next: RevisionRequest = {
      municipalityReferenceNumber: parsed.municipalityReferenceNumber,
      reviewerName: parsed.reviewerName,
      reviewerDepartment: parsed.reviewerDepartment,
      receivedDate: parsed.receivedDate,
      responseDeadline: parsed.responseDeadline,
      severity: parsed.severity,
      assignedOwner: parsed.assignedOwner,
      notes: parsed.notes,
      items: existing?.items ?? [],
    };
    return { ...pd, revisionRequest: next };
  });
}

/**
 * Append a new revision item. If no revision request exists yet, the
 * caller must seed metadata first via `setRevisionRequestMetadata`.
 */
export function addRevisionItem(job: Job, input: RevisionItemInput): Job {
  const parsed = revisionItemInputSchema.parse(input);
  return withPermitDetail(job, (pd) => {
    if (!pd.revisionRequest) return pd;
    const item: RevisionItem = {
      id: makeRevisionItemId(),
      documentType: parsed.documentType,
      issueDescription: parsed.issueDescription,
      requiredAction: parsed.requiredAction,
      resolved: false,
    };
    return {
      ...pd,
      revisionRequest: {
        ...pd.revisionRequest,
        items: [...pd.revisionRequest.items, item],
      },
    };
  });
}

/**
 * Toggle a single revision item's resolved state. When marking
 * resolved, stamps `resolvedAt` (now ISO) and `resolvedBy`. When
 * un-resolving, clears both.
 */
export function setRevisionItemResolved(
  job: Job,
  itemId: string,
  resolved: boolean,
  resolvedBy: string,
): Job {
  return withPermitDetail(job, (pd) => {
    if (!pd.revisionRequest) return pd;
    const items = pd.revisionRequest.items.map((it) => {
      if (it.id !== itemId) return it;
      if (resolved) {
        return {
          ...it,
          resolved: true,
          resolvedAt: new Date().toISOString(),
          resolvedBy,
        };
      }
      const { resolvedAt: _ra, resolvedBy: _rb, ...rest } = it;
      void _ra;
      void _rb;
      return { ...rest, resolved: false };
    });
    return {
      ...pd,
      revisionRequest: { ...pd.revisionRequest, items },
    };
  });
}
